import React from "react";

import type * as GraphqlTypes from "@shared/graphql.types";
import { Typography, MarkdownContainer } from "@shared/ui.primitives";
import { AmIcon, PmIcon } from "@shared/ui.icons";

import { VideoPlayer } from "components/VideoPlayer";

import * as styles from "./HowToUse.css";
import { useTrackElementViewed } from "packages/membership/tracking";

export const mapIcons = {
  "Time AM": <AmIcon className={styles.icon} />,
  "Time PM": <PmIcon className={styles.icon} />
};

const baseTrackingValues = {
  target: "PDP How to use",
  location: "PDP"
};

export const HowToUse: React.FC<GraphqlTypes.HowToUse> = ({
  title,
  description,
  caution,
  icons,
  video
}) => {
  const { ref } = useTrackElementViewed(baseTrackingValues);

  return (
    <>
      <div className={styles.container} ref={ref}>
        <div className={styles.item}>
          <Typography as="h3" variant="subheader-3" colour="primary-dark">
            {title}
          </Typography>

          {icons && icons.length > 0 && (
            <div className={styles.iconsContainer}>
              {icons.map((icon, index) => (
                <div key={index} className={styles.iconWrapper}>
                  {mapIcons[icon.type]}
                  <Typography
                    as="p"
                    variant="smallprint-sm-bold"
                    colour="primary-dark"
                  >
                    {icon.title}
                  </Typography>
                </div>
              ))}
            </div>
          )}

          <MarkdownContainer
            content={description}
            tagOverrides={{
              h2: {
                component: Typography,
                props: {
                  as: "h2",
                  variant: "subheader-6",
                  colour: "primary-dark"
                }
              },
              h4: {
                component: Typography,
                props: {
                  as: "h6",
                  variant: "subheader-6",
                  colour: "primary-dark"
                }
              },
              p: {
                component: Typography,
                props: {
                  as: "p",
                  variant: "body-md",
                  colour: "primary-dark"
                }
              },
              span: {
                component: Typography,
                props: {
                  as: "span",
                  variant: "body-md",
                  colour: "primary-dark"
                }
              },
              b: {
                component: Typography,
                props: {
                  as: "strong",
                  variant: "body-md-bold",
                  colour: "primary-dark"
                }
              }
            }}
            forceBlock={true}
          />
        </div>

        {video && (
          <div className={styles.item}>
            <VideoPlayer
              description={video.description ?? undefined}
              options={{
                html5: {
                  nativeTextTracks: false
                },
                playsinline: true,
                controls: true,
                loop: true,
                responsive: true,
                fluid: true,
                aspectRatio: "1:1",
                sources: video.sources.map(({ uri, contentType }) => ({
                  src: uri,
                  type: contentType
                }))
              }}
              videoProps={{
                title: video.title ?? undefined,
                "aria-label": video.title ?? undefined
              }}
              coverImageProps={{
                src: video.imageUrl,
                title: video.title ?? undefined,
                alt: video.title ?? "",
                sizes: "(max-width: 1024px) 100vw, 50vw"
              }}
              videoPosition="PDP how to"
              muted={true}
              autoPlayWhenScrolledIntoView
            />
          </div>
        )}
      </div>
      <div className={styles.cautionContainer}>
        {caution && (
          <>
            <Typography as="h6" variant="subheader-6" colour="primary-dark">
              {caution.title}
            </Typography>
            <MarkdownContainer
              content={caution.description}
              tagOverrides={{
                h2: {
                  component: Typography,
                  props: {
                    as: "h2",
                    variant: "subheader-6",
                    colour: "primary-dark"
                  }
                },
                h4: {
                  component: Typography,
                  props: {
                    as: "h6",
                    variant: "subheader-6",
                    colour: "primary-dark"
                  }
                },
                p: {
                  component: Typography,
                  props: {
                    as: "p",
                    variant: "body-md",
                    colour: "primary-dark"
                  }
                },
                span: {
                  component: Typography,
                  props: {
                    as: "span",
                    variant: "body-md",
                    colour: "primary-dark"
                  }
                },
                b: {
                  component: Typography,
                  props: {
                    as: "strong",
                    variant: "body-md-bold",
                    colour: "primary-dark"
                  }
                }
              }}
              forceBlock={true}
            />
          </>
        )}
      </div>
    </>
  );
};
